import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { TableRow } from "../utils/tables-metadata";
import { LabTable } from "./lab";
import { BrowseNotesTable, BrowseImagingReportsTable } from "./notes";
import { RawDocumentsTable } from "./raw-documents";
import { renderSharedTable } from "./shared-table-content";

interface BrowseTableContentProps {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  fhirSdk: FhirBundleSdk;
  handleRowClick: (row: TableRow, tableType: string, suspectsContext?: string) => void;
  handlePdfView: (docId: string) => void;
  isRowViewed: (tableType: string, rowId: string) => boolean;
  onDocumentDownloaded?: (docId: string) => void;
}

/**
 * Table dispatcher for browse mode.
 * Uses shared rendering for common tables, with browse-specific handling for:
 * - DiagnosticReport: BrowseNotesTable
 * - ImagingReports: BrowseImagingReportsTable
 * - LaboratoryObservations: LabTable with Individual/Panel toggle
 * - DocumentReference: RawDocumentsTable with no filter query
 */
export function BrowseTableContent({
  tableType,
  displayName,
  rows,
  fhirSdk,
  handleRowClick,
  handlePdfView,
  isRowViewed,
  onDocumentDownloaded,
}: BrowseTableContentProps): JSX.Element | null {
  const isRowViewedHelper = (row: TableRow) => isRowViewed(tableType, row.id);

  const commonProps = {
    fhirSdk,
    filterQuery: "",
    onPdfView: handlePdfView,
    onRowClick: (row: TableRow) => handleRowClick(row, tableType),
    isRowViewed: isRowViewedHelper,
  };

  // Try shared table rendering first
  const sharedResult = renderSharedTable({ tableType, displayName, rows, commonProps });
  if (sharedResult !== null) {
    return sharedResult;
  }

  // Browse-specific table handling
  switch (tableType) {
    case "DiagnosticReport":
      return <BrowseNotesTable rows={rows} {...commonProps} displayName={displayName} />;

    case "ImagingReports":
      return <BrowseImagingReportsTable rows={rows} {...commonProps} displayName={displayName} />;

    case "LaboratoryObservations":
      // Browse mode: LabTable WITH toggle (allows switching between Individual/Panel views)
      return <LabTable rows={rows} displayName={displayName} {...commonProps} />;

    case "DocumentReference":
      return (
        <RawDocumentsTable
          rows={rows}
          fhirSdk={fhirSdk}
          filterQuery=""
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onRowClick={() => {}}
          onDocumentDownloaded={onDocumentDownloaded}
          isRowViewed={isRowViewedHelper}
        />
      );

    default:
      console.warn(`Unknown tableType in browse mode: ${tableType}`);
      return null;
  }
}
